// src/oura/components/site/siteIssues.ts
//
// The crawl findings as the Site screen shows them: with the reviewer's
// out-of-scope pages removed and said so, ordered worst first, and with the
// operator's progress beside the crawl's own count.

import type { AuditIssue, IssueSeverity } from "../../audit";
import type { Tone } from "@oura/ui";
import { pageUrlKey } from "../../lib/oura";
import { isSetAsideRole, type PageRole } from "../../pageWorkshop";

/** An audit finding with the reviewer's out-of-scope pages already removed, and
 *  `setAside` recording how many, so the screen can say so instead of just
 *  showing a smaller number than the backend holds. */
export type ScopedIssue = AuditIssue & { setAside: number };

export const SEVERITY_ORDER: IssueSeverity[] = ["error", "warning", "notice"];

/** Rose, amber and grey, from the backend's own severity, never a sample colour. */
export const SEVERITY_TONE: Record<IssueSeverity, Tone> = {
    error: "rose",
    warning: "amber",
    notice: "gray",
};

export const SEVERITY_WORD: Record<IssueSeverity, string> = {
    error: "Error",
    warning: "Warning",
    notice: "Notice",
};

/** Every role a reviewer set, by the join key two lists can share. */
export function rolesByKey(roles: Record<string, PageRole>): Map<string, PageRole> {
    return new Map(Object.entries(roles).map(([url, role]) => [pageUrlKey(url), role]));
}

/**
 * Every page a reviewer set aside: `product`, `utility` AND `placeholder`.
 *
 * All three are DROPPED from a finding's affected pages, not shown in a
 * separate tab: a page marked out of scope on the Pages tab does not need
 * technical work here, whatever the reason it was set aside, and listing it,
 * even under an "out of scope" heading, puts it back on the worklist the
 * reviewer just took it off. The count they were removed from still says "N out
 * of scope", so the number stays honest without the rows being present.
 */
export function setAsideKeys(roles: Map<string, PageRole>): Set<string> {
    return new Set(Array.from(roles).filter(([, role]) => isSetAsideRole(role)).map(([key]) => key));
}

/**
 * Every finding with its out-of-scope pages removed, and `setAside` saying
 * how many went.
 *
 * The count is stated rather than silently reduced. The header number and
 * the rows beneath it disagreeing is the exact bug #4444 was filed for, and
 * "28 pages" with 30 in the backend is the same disagreement wearing a
 * different hat.
 */
export function scopeIssues(issues: AuditIssue[], setAside: Set<string>): ScopedIssue[] {
    return issues.map((i) => {
        if (setAside.size === 0) return { ...i, setAside: 0 };
        const pages = i.pages.filter((p) => !setAside.has(pageUrlKey(p.url)));
        const removed = i.pages.length - pages.length;
        return { ...i, pages, count: Math.max(0, i.count - removed), setAside: removed };
    });
}

/**
 * The findings worth listing, worst first: severity, then how many pages.
 *
 * The BE always emits every check so the contract shape is stable, including
 * the ones that found nothing, and a 0-page check under "Warnings" reads as a
 * problem when it is the opposite. Only checks that found something are kept.
 *
 * Filtered on the finding's ORIGINAL size (count + setAside). A check whose
 * every affected page was set aside still FOUND something, and dropping it
 * would tell the reader the site is clean on a check it failed, which is a
 * stronger claim than "you chose not to work on these pages".
 */
export function activeIssues(scoped: ScopedIssue[]): ScopedIssue[] {
    return scoped
        .filter((i) => i.count + i.setAside > 0)
        .sort((a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity) || b.count - a.count);
}

/**
 * Affected pages across the findings of one severity: a page carrying two
 * findings counts twice, the way the crawl reports it. The Issues tile and
 * the Issues tab both state errors plus warnings from this, so the two
 * numbers on screen are one number.
 */
export function pagesWith(issues: ScopedIssue[], severity: IssueSeverity): number {
    return issues.filter((i) => i.severity === severity).reduce((n, i) => n + i.count, 0);
}

/**
 * #3515: progress WITHOUT counting down. The total is crawl truth and only
 * moves when the next crawl re-measures the site (ourteam-backend#4528):
 * work an operator marks done must never make a still-present problem look
 * solved. So both numbers are shown, and the total stays authoritative.
 *
 * Derived from operatorStatus on the rows the payload already carries, the
 * same field IssuePagesRemediation.statusOf() reads, so the table and the
 * drawer agree by construction.
 */
export function progressText(issue: ScopedIssue): { count: string; progress: string | null } {
    const done = issue.pages.filter((p) => p.operatorStatus === "done").length;
    const count = `${issue.count}`;
    if (done === 0) return { count, progress: null };
    return { count, progress: done >= issue.count ? "All done" : `${issue.count - done} left` };
}
